<template>
  <dashboard-card class="db-overview" title="Visão geral" :cut="scope">
    <div class="db-kpis">
      <router-link class="db-kpi db-kpi-primary db-kpi-link" :to="productionTo"
                   :class="{'is-zero': overview.production.value === 0}">
        <span class="db-kpi-label">
          Produção total
          <fa-icon class="db-kpi-go" :icon="['fal', 'arrow-right']"/>
        </span>
        <div class="db-kpi-value db-num">
          <span class="db-sk db-sk-lg" v-if="loading"></span>
          <template v-else>{{ overview.production.value | currency }}</template>
        </div>
        <span class="db-kpi-hint">{{ productionHint }}</span>
      </router-link>

      <router-link class="db-kpi db-kpi-green db-kpi-link" :to="billedTo"
                   :class="{'is-zero': overview.billed.value === 0}">
        <span class="db-kpi-label">
          Total faturado
          <fa-icon class="db-kpi-go" :icon="['fal', 'arrow-right']"/>
        </span>
        <div class="db-kpi-value db-num">
          <span class="db-sk db-sk-lg" v-if="loading"></span>
          <template v-else>{{ overview.billed.value | currency }}</template>
        </div>
        <span class="db-kpi-hint">{{ billedHint }}</span>
      </router-link>

      <router-link class="db-kpi db-kpi-amber db-kpi-link" :to="waitingRoute"
                   :class="{'is-zero': overview.waiting.value === 0}">
        <span class="db-kpi-label">
          Aguardando faturamento
          <fa-icon class="db-kpi-go" :icon="['fal', 'arrow-right']"/>
        </span>
        <div class="db-kpi-value db-num">
          <span class="db-sk db-sk-lg" v-if="loading"></span>
          <template v-else>{{ overview.waiting.value | currency }}</template>
        </div>
        <span class="db-kpi-hint">{{ waitingHint }}</span>
      </router-link>

      <router-link class="db-kpi db-kpi-red db-kpi-link" :to="glossesTo"
                   :class="{'is-zero': overview.glossed.value === 0}">
        <span class="db-kpi-label">
          Glosas recebidas
          <fa-icon class="db-kpi-go" :icon="['fal', 'arrow-right']"/>
        </span>
        <div class="db-kpi-value db-num">
          <span class="db-sk db-sk-lg" v-if="loading"></span>
          <template v-else>{{ overview.glossed.value | currency }}</template>
        </div>
        <span class="db-kpi-hint">{{ glossedHint }}</span>
      </router-link>

      <router-link class="db-kpi db-kpi-blue db-kpi-link" :to="expectedTo"
                   :class="{'is-zero': overview.expected.value === 0}">
        <span class="db-kpi-label">
          Previsão a receber
          <fa-icon class="db-kpi-go" :icon="['fal', 'arrow-right']"/>
        </span>
        <div class="db-kpi-value db-num">
          <span class="db-sk db-sk-lg" v-if="loading"></span>
          <template v-else>{{ overview.expected.value | currency }}</template>
        </div>
        <span class="db-kpi-alert" v-if="!loading && hasOverdue">{{ overdueHint }}</span>
        <span class="db-kpi-hint" v-else>{{ expectedHint }}</span>
      </router-link>
    </div>
  </dashboard-card>
</template>

<script>
import currency from '@/filters/currency';
import {
  billedRoute, expectedRoute, formatPercent, glossesRoute, guidesRoute, percentOf, pluralize,
  productionRoute,
} from '../helpers';
import DashboardCard from './DashboardCard.vue';

export default {
  components: {
    DashboardCard,
  },
  props: {
    overview: {
      type: Object,
      required: true,
    },
    filter: {
      type: Object,
      required: true,
    },
    scope: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    productionTo() {
      return productionRoute(this.filter);
    },
    billedTo() {
      return billedRoute(this.filter);
    },
    glossesTo() {
      return glossesRoute(this.filter);
    },
    expectedTo() {
      return expectedRoute(this.filter);
    },
    waitingRoute() {
      return guidesRoute(null, this.filter);
    },
    hasOverdue() {
      return this.overview.expected.overdueValue > 0
        || this.overview.expected.overdueCount > 0;
    },
    productionHint() {
      if (this.loading) return '';
      const { count } = this.overview.production;
      if (count === 0) return 'nenhum item de convênio';
      return pluralize(count, 'item de convênio', 'itens de convênio');
    },
    billedHint() {
      if (this.loading) return '';
      const { batches } = this.overview.billed;
      if (this.overview.production.value > 0) {
        const share = formatPercent(
          percentOf(this.overview.billed.value, this.overview.production.value), 0,
        );
        return `${share} da produção`;
      }
      if (batches === 0) return 'nenhum lote no período';
      return pluralize(batches, 'lote enviado', 'lotes enviados');
    },
    waitingHint() {
      if (this.loading) return '';
      const { count } = this.overview.waiting;
      if (count === 0) return 'tudo faturado';
      return pluralize(count, 'guia aguardando lote', 'guias aguardando lote');
    },
    glossedHint() {
      if (this.loading) return '';
      const { count, percent } = this.overview.glossed;
      if (count === 0) return 'nenhuma glosa recebida';
      return `${formatPercent(percent)} do faturado`;
    },
    expectedHint() {
      if (this.loading) return '';
      if (this.overview.expected.value === 0) return 'nada previsto no período';
      return 'lotes em aberto';
    },
    overdueHint() {
      const { overdueValue, overdueCount } = this.overview.expected;
      const label = pluralize(overdueCount, 'lote vencido', 'lotes vencidos');
      return `${currency(overdueValue)} em atraso · ${label}`;
    },
  },
};
</script>
